import type { ExploreSingleInsightWithMeta } from '@api/api/insights';
import { getConditionIcons } from '@terminal/components/insight-icons/utils';
import { InsightIconsRoot } from '@terminal/components/insight-icons-root/InsightIconsRoot';
import { InsightsTooltip } from '@terminal/components/insights-tooltip/InsightsTooltip';
import { isToday } from 'date-fns';
import { useTranslation } from 'react-i18next';

import styles from './PriceInsightMarker.module.scss';

const SINGLE_ICON_SIZE = {
  width: '12px',
  height: '14px',
};

const COMBINED_ICON_SIZE = {
  width: '12px',
  height: '24px',
};

export interface PriceInsightMarkerProps {
  insight: ExploreSingleInsightWithMeta;
  active: boolean;
  onClick: (id: string) => void;
}

export const PriceInsightMarker = ({
  insight,
  active,
  onClick,
}: PriceInsightMarkerProps) => {
  const { t } = useTranslation(['insights']);

  const conditions = getConditionIcons(insight);
  const isCombined = conditions.length > 1;
  const isStarred = isToday(new Date(insight.created_at));
  const iconProps = {
    conditions,
    direction: insight.direction,
    customSize: isCombined ? COMBINED_ICON_SIZE : SINGLE_ICON_SIZE,
    isStarred,
    isCombined,
    isMatched: true,
    layout: 'vertical' as const,
    isSparklesCondensed: true,
  };
  const episodeLabel = t('insights:insightCard.episode', {
    count: insight.episode_count,
  });

  return (
    <InsightsTooltip
      {...iconProps}
      horizon={insight.best_horizon}
      episodeLabel={episodeLabel}
      median={insight.median}
      stars={insight.stars}
      name={insight.card.name}
      entityTag={insight.metadata?.entity}
    >
      <button
        type="button"
        className={styles.marker}
        data-testid="chart-insight-icons"
        aria-label={`${t('insights:show')} ${insight.card.name}`}
        onClick={() => onClick(insight.id)}
      >
        {active && (
          <span
            className={styles.dateDottedLine}
            data-testid="date-dotted-line"
          />
        )}
        <span className={styles.icon}>
          <InsightIconsRoot {...iconProps} />
        </span>
      </button>
    </InsightsTooltip>
  );
};
